/* eslint-disable no-restricted-globals */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-plusplus */
import { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DataSheetGrid, keyColumn } from 'react-datasheet-grid';
import 'react-datasheet-grid/dist/style.css';
import { FiSend } from 'react-icons/fi';

import './DataSheet.scss';
import { postQuantitiesSimplifiedCharging } from '../../services/monthlyServiceRequest';
import useToast from '../../hooks/useToast';

import {
  isValidDate,
  newLineToPush,
  getQuantitesToPost,
  isAllReturnedLineEdited,
  setReturnedsPJLFunc
} from './utils';
import { SendToApproveModal } from './SendToApproveModal';

const DataSheet = ({
  columns,
  id,
  initialGroups,
  isOrderByUbication,
  linesByPostionLIne,
  linesByUbication,
  onSend,
  setLoading,
  disabled
}) => {
  const initialReturnedLinesWithouMap = setReturnedsPJLFunc(
    isOrderByUbication,
    linesByUbication,
    linesByPostionLIne
  );

  const [groups, setGroups] = useState(initialGroups);
  const [linesToPost, setLinesToPost] = useState(initialReturnedLinesWithouMap);
  const [isReturnedLinesWithOutEdit, setIsReturnedLinesWithOutEdit] = useState(
    !!getQuantitesToPost(initialReturnedLinesWithouMap).length
  );
  const [showSendToApproveModal, setShowSendToApproveModal] = useState(false);
  const { setNewToast } = useToast();

  const columnas = columns.map((column) => ({
    ...keyColumn(column.keyColumn, column.typeOfColumn),
    title: column.title
  }));

  // Opened version of groups
  const rows = useMemo(() => {
    const result = [];

    for (let i = 0; i < groups.length; i++) {
      for (let j = 0; j < groups[i].children.length; j++) {
        result.push({
          type: 'CHILD',
          groupIndex: i,
          childIndex: j,
          ...groups[i].children[j]
        });
      }
    }

    return result;
  }, [groups]);

  // Recibe la fila que cambio, con su copia antigua sin cambios
  // Con esto verificamos que columnas cambiaron efectivamente
  const returnKeysWithChanges = (oldRow, newRow) =>
    Object.keys(newRow).filter(
      (key) =>
        isValidDate(key) &&
        (newRow[key]?.unitMeasureValue !== oldRow[key]?.unitMeasureValue ||
          (typeof newRow[key] === 'number' && isNaN(newRow[key]) === false))
    );

  const getNewQuantity = (unitMeasureValue) => {
    const quantity = parseFloat(unitMeasureValue);

    return quantity >= 0 ? quantity : null;
  };

  const addNewUbication = (
    lineToPostAux,
    newUbicationId,
    newPartJobLineId,
    rowKey,
    newQuantity
  ) => {
    lineToPostAux.push(
      newLineToPush(newUbicationId, newPartJobLineId, rowKey.dailyPartJobLineId, newQuantity)
    );
  };

  const addNewPartJobLine = (lineas, newPartJobLineId, dailyPartJobLineId, newQuantity) => {
    lineas.push({
      parte_trabajo_linea_id: newPartJobLineId,
      cantidades: [{ id: dailyPartJobLineId, cantidad: newQuantity }]
    });
  };

  const newPartJobLineQuantity = (cantidades, newQuantity, newRow, key) => {
    cantidades.push({
      id: newRow[key].dailyPartJobLineId,
      cantidad: newQuantity
    });
  };

  const updateUbication = (
    lineToPostAux,
    ubicationIds,
    newPartJobLineId,
    newUbicationId,
    key,
    newRow,
    newQuantity
  ) => {
    const index = ubicationIds.indexOf(newUbicationId);
    const ubication = lineToPostAux[index];
    const { lineas } = ubication;
    const lineaIds = lineas.map((linea) => linea.parte_trabajo_linea_id);

    if (!lineaIds.includes(newPartJobLineId)) {
      addNewPartJobLine(lineas, newPartJobLineId, newRow[key].dailyPartJobLineId, newQuantity);
    } else {
      const indexLines = lineaIds.indexOf(newPartJobLineId);
      const linea = lineas[indexLines];
      const { cantidades } = linea;
      const cantidadIds = cantidades.map((cantidad) => cantidad.id);

      if (!cantidadIds.includes(newRow[key].dailyPartJobLineId)) {
        newPartJobLineQuantity(cantidades, newQuantity, newRow, key);
      } else {
        const indexQuants = cantidadIds.indexOf(newRow[key].dailyPartJobLineId);

        if (newQuantity !== null) {
          cantidades[indexQuants].cantidad = newQuantity;
        } else if (cantidades.length > 1) {
          cantidades.splice(indexQuants, 1);
        } else {
          lineas.splice(indexLines, 1);
          if (lineas.length === 0) {
            lineToPostAux.splice(index, 1);
          }
        }
      }
    }
  };

  // Recibe la fila que recibio una actualizacion y actualiza el estado
  // KeyWithChanges es un array con las keys que cambiaron
  // estas hacen referencia a las columnas que cambiaron
  const saveChangesForPost = (newRow, keysWithChanges, result) => {
    const lineToPostAux = [...result];
    const newUbicationId = newRow.group.ubicationId || newRow.rowDefiner.ubicationId;
    const newPartJobLineId = newRow.rowDefiner.partJobLineId || newRow.group.partJobLineId;

    keysWithChanges.forEach((key) => {
      const newQuantity = getNewQuantity(newRow[key].unitMeasureValue);

      const ubicationIds = lineToPostAux.map((ubication) => ubication.ubicacion_id);

      if (!ubicationIds.includes(newUbicationId)) {
        addNewUbication(lineToPostAux, newUbicationId, newPartJobLineId, newRow[key], newQuantity);
      } else {
        updateUbication(
          lineToPostAux,
          ubicationIds,
          newPartJobLineId,
          newUbicationId,
          key,
          newRow,
          newQuantity
        );
      }
    });

    return lineToPostAux;
  };

  // Recibe la fila que cambio y la key que representa la columna que cambio
  // Y verifica que el valor que tipamos en la celda es valido
  const validValueOfCell = (newRow, key) => {
    let value;

    if (newRow[key]?.unitMeasureValue === '') {
      value = null;
    } else {
      // newRow[key]?.unitMeasureValue - Se da cuando viene un valor tipeado directamente en la celda
      // newRow[key] - Se da cuando el valor se copio y pego otra celda
      value = newRow[key]?.unitMeasureValue >= 0 ? newRow[key]?.unitMeasureValue : newRow[key];
    }

    return value;
  };

  const setNewRow = (newRow, keysWithChanges) => {
    const oldRow = groups[newRow.groupIndex].children[newRow.childIndex];
    const newRowToChange = oldRow;

    keysWithChanges.forEach((key) => {
      newRowToChange[key] = {
        ...oldRow[key],
        unitMeasureValue: validValueOfCell(newRow, key)
      };
    });

    return newRowToChange;
  };

  const handleChange = (newRows, operations) => {
    const result = [...linesToPost];

    for (const operation of operations) {
      if (operation.type === 'UPDATE') {
        const resultado = newRows
          .slice(operation.fromRowIndex, operation.toRowIndex)
          .reduce((acc, row) => {
            if (row.type === 'CHILD') {
              const keysWithChanges = returnKeysWithChanges(
                groups[row.groupIndex].children[row.childIndex],
                row
              );
              const newRow = setNewRow(row, keysWithChanges);

              const saveChanges = saveChangesForPost(newRow, keysWithChanges, acc);

              groups[row.groupIndex].children[row.childIndex] = { ...newRow };

              return saveChanges;
            }

            return acc;
          }, result);

        setLinesToPost([...resultado]);
      }

      setGroups([...groups]);
    }
  };

  useEffect(() => {
    setGroups(initialGroups);
  }, [initialGroups]);

  const handleSendToApprove = async () => {
    try {
      setLoading(true);
      const response = await postQuantitiesSimplifiedCharging(id, getQuantitesToPost(linesToPost));

      setNewToast({
        type: 'SUCCESS',
        message: response.mensaje
      });
      setShowSendToApproveModal(false);
      onSend();
    } catch (err) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
  };

  const hasReturndesPJLinesWithOutEdit = () => {
    if (getQuantitesToPost(initialReturnedLinesWithouMap).length === 0) return;
    // formateo de cantidades ingresadas/editadas en excel
    // => {id, cantidadEditada/Ingresada}
    const quantitiesToPost = getQuantitesToPost(linesToPost);
    const returnedsPJlinesIds = getQuantitesToPost(initialReturnedLinesWithouMap).map(
      (line) => line.id
    );

    const editedReturnedLines = quantitiesToPost.filter((line) =>
      returnedsPJlinesIds.includes(line.id)
    );

    if (editedReturnedLines.length === 0) {
      return;
    }

    setIsReturnedLinesWithOutEdit(
      !isAllReturnedLineEdited(
        getQuantitesToPost(initialReturnedLinesWithouMap),
        editedReturnedLines
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  useEffect(() => {
    hasReturndesPJLinesWithOutEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linesToPost]);

  return (
    <>
      <DataSheetGrid
        className="simplified-charging-datasheet"
        columns={columnas}
        gutterColumn={false}
        height={window.screen.width > 1400 ? 800 : 515}
        lockRows
        onChange={handleChange}
        rowHeight={60}
        value={rows}
      />
      {!disabled && (
        <button
          aria-label="Enviar"
          className="button-send"
          disabled={disabled}
          onClick={() => {
            setShowSendToApproveModal(true);
          }}
          type="button"
        >
          <FiSend />
        </button>
      )}
      <SendToApproveModal
        handleSendToApprove={handleSendToApprove}
        isReturnedLinesWithOutEdit={isReturnedLinesWithOutEdit}
        noLinesToSend={getQuantitesToPost(linesToPost).length === 0}
        setShowSendToApproveModal={setShowSendToApproveModal}
        showSendToApproveModal={showSendToApproveModal}
      />
    </>
  );
};

DataSheet.propTypes = {
  initialGroups: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      children: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any))
    })
  ),
  disabled: PropTypes.bool.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      keyColumn: PropTypes.string,
      typeOfColumn: PropTypes.shape({}),
      titleColumn: PropTypes.string
    })
  ),
  id: PropTypes.string.isRequired,
  onSend: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  isOrderByUbication: PropTypes.string.isRequired,
  linesByUbication: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      lineas: PropTypes.arrayOf(
        PropTypes.shape({
          cantidades: PropTypes.arrayOf(
            PropTypes.shape({
              cantidad: PropTypes.number,
              color: PropTypes.string,
              estadoEnum: PropTypes.number,
              fecha: PropTypes.string,
              idParteTrabajoDiario: PropTypes.string
            })
          ),
          idParteTrabajoLinea: PropTypes.number,
          idUbicacionLinea: PropTypes.number,
          nombreLineaSap: PropTypes.string,
          nombreLineaYClick: PropTypes.string,
          nombrePosicionSap: PropTypes.string,
          nombrePosicionYClick: PropTypes.string,
          numeroLinea: PropTypes.number,
          numeroPosicion: PropTypes.number,
          unidadMedida: PropTypes.string
        })
      )
    })
  ),
  linesByPostionLIne: PropTypes.arrayOf(
    PropTypes.shape({
      idParteTrabajoLinea: PropTypes.number,
      nombreLineaSap: PropTypes.string,
      nombreLineaYClick: PropTypes.string,
      nombrePosicionSap: PropTypes.string,
      nombrePosicionYClick: PropTypes.string,
      numeroLinea: PropTypes.number,
      numeroPosicion: PropTypes.number,
      ubicaciones: PropTypes.arrayOf(
        PropTypes.shape({
          cantidades: PropTypes.arrayOf(
            PropTypes.shape({
              cantidad: PropTypes.number,
              color: PropTypes.string,
              estadoEnum: PropTypes.number,
              fecha: PropTypes.string,
              idParteTrabajoDiario: PropTypes.number
            })
          ),
          idUbicacion: PropTypes.number,
          ubicacionNombre: PropTypes.string
        })
      ),
      unidadMedida: PropTypes.string
    })
  )
};

DataSheet.defaultProps = {
  initialGroups: [],
  columns: [],
  linesByPostionLIne: [],
  linesByUbication: []
};

export default DataSheet;
